/* eslint-disable @next/next/no-img-element */
"use client";
import Markdown from "react-markdown";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { Button } from "@/components/fun-ui/Button";
import { useGuides, usePayment, useHelpPopup } from "@/stores/appStore";
import { FeatureGuide, Guides, GuidesScene } from "@/stores/slices/guideSlice";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { externalMarkdownLink } from "@/components/md-components";

type GuidesDialogProps = {
  autoOpen?: boolean;
  scene: GuidesScene;
  onClose?: (type: string) => void;
  onDone?: (type: string) => void;
};
export function GuidesDialog({
  autoOpen,
  scene,
  onClose = () => { },
  onDone = () => { },
}: GuidesDialogProps) {
  const [open, setOpen] = useState<boolean | undefined>(undefined);
  const { guides, fetchGuides, markGuidesViewed, guidesMap } = useGuides();
  const { openPayment } = usePayment();
  const { setOpen: setHelpPopupOpen } = useHelpPopup();
  const buyingDialogOpenedRef = useRef(false);
  const helpPopupHandledRef = useRef(false);
  const initializedSceneRef = useRef<string | null>(null);
  const fetchGuidesRef = useRef(fetchGuides);
  const setHelpPopupOpenRef = useRef(setHelpPopupOpen);

  // 同步 ref
  useEffect(() => {
    fetchGuidesRef.current = fetchGuides;
    setHelpPopupOpenRef.current = setHelpPopupOpen;
  }, [fetchGuides, setHelpPopupOpen]);

  // 初始请求 - 只在 scene 变化时执行
  useEffect(() => {
    // 如果已经初始化过相同的 scene，跳过
    if (initializedSceneRef.current === scene) return;
    initializedSceneRef.current = scene;
    helpPopupHandledRef.current = false;
    fetchGuidesRef.current(scene);
  }, [scene]);

  // 监听 guides 变化，处理 help 类型和其他类型
  useEffect(() => {
    if (guides?.type === 'help' && !helpPopupHandledRef.current) {
      helpPopupHandledRef.current = true;
      setHelpPopupOpenRef.current(true, scene);
    } else if (guides?.type && guides?.type !== 'help' && guides?.type !== 'buying' && open === undefined) {
      setOpen(true);
    }
  }, [guides?.type, scene, open]);

  useEffect(() => {
    // 完成、关闭
    if (open === false) {
      markGuidesViewed(scene);
    }
  }, [markGuidesViewed, open, scene]);

  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
    }
  }, [autoOpen])

  const renderGuidesContent = useCallback((guides: Guides) => {
    if (guides?.type === 'features') {
      return (
        <FeatureGuides
          guides={guides.value}
          onDone={() => {
            setOpen(false);
            onDone(guides?.type);
          }}
        />
      )
    }
    if (guides?.type === 'gift') {
      return (
        <MeetingGiftGuides
          url={guides?.value?.url}
          onDone={() => {
            setOpen(false);
            onDone(guides?.type);
          }}
        />
      );
    }

    if (['ai_col_charge', 'receive_vip_alert'].includes(guides?.type)) {
      return (
        <CommonImageContent
          {...guides?.value}
          onDone={() => {
            setOpen(false);
            onDone(guides?.type);
          }}
        />
      )
    }
  }, [onDone]);

  // 处理 buying 类型：直接打开 PaymentDialog 的 upgrade tab
  useEffect(() => {
    if (guides?.type === 'buying' && !buyingDialogOpenedRef.current) {
      buyingDialogOpenedRef.current = true;
      // 打开支付对话框，设置为 upgrade tab
      // 设置 callback，在 PaymentDialog 关闭时调用 markGuidesViewed，然后请求新的 guides
      // fetchGuides 完成后会更新 guides 状态，触发上面的 useEffect 来处理 help 类型
      openPayment({ open: true, type: 'upgrade' }, () => {
        // PaymentDialog 关闭后，执行异步操作
        (async () => {
          await markGuidesViewed(scene);
          buyingDialogOpenedRef.current = false;
          helpPopupHandledRef.current = false; // 重置标志，允许处理新的 help 类型
          await fetchGuidesRef.current(scene);
        })();
      });
    }
  }, [guides?.type, openPayment, markGuidesViewed, scene]);

  // buying 和 help 类型不渲染 GuidesDialog
  if (guides?.type === 'buying' || guides?.type === 'help') {
    return <></>;
  }

  if (!guides?.type || ![
    'features', 'gift'/*, 'coupons'*/, 'ai_col_charge', 'receive_vip_alert'
  ].includes(guides?.type)) {
    return <></>;
  }

  return (
    <Dialog
      open={!!open}
      onOpenChange={async (open) => {
        setOpen(open);
        if (!open) {
          onClose?.(guides?.type);
        }
      }}
    >
      <DialogContent
        className={cn(
          "w-full sm:max-w-fit p-0 m-0 h-auto border-none bg-transparent focus-visible:outline-none focus-visible:ring-0 overflow-hidden gap-0",
        )}
        tabIndex={-1}
        onPointerDownOutside={(evt) => {
          evt.preventDefault();
        }}
        onOpenAutoFocus={(evt) => {
          evt.preventDefault();
        }}
      >
        {
          renderGuidesContent(guides!)
        }
        <DialogClose />
      </DialogContent>
    </Dialog>
  );
}

type FeatureGuidesProps = {
  guides: FeatureGuide[];
  onDone: () => void;
}

const FeatureGuides = ({
  guides: featureGuides,
  onDone = () => { },
}: FeatureGuidesProps) => {
  const [currentPage, setCurrentPage] = useState(0);

  const goToNextPage = () => {
    if (currentPage < featureGuides.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="max-h-fit h-full w-full flex items-center justify-center overflow-hidden bg-background focus-visible:outline-none focus-visible:ring-0 max-w-[720px]" tabIndex={-1}>
      <div className="max-h-fit h-full flex transition-transform duration-300 max-w-full" style={{ transform: `translateX(-${currentPage * 100}%)` }}>
        {featureGuides.map((feature, index) => (
          <div key={index} className="max-h-fit h-full w-full flex-shrink-0 items-center justify-center">
            <div className="w-full aspect-video">
              <img
                src={feature.url}
                alt={feature.caption}
                className="object-cover h-full"
              />
            </div>
            <div className="p-7">
              <div className="flex justify-between">
                <h2 className="font-medium text-2xl">{feature.caption}</h2>
                <span className="font-normal text-base text-muted-foreground">{currentPage + 1}/{featureGuides.length}</span>
              </div>
              <div className="font-normal text-base text-muted-foreground mt-3.5"><Markdown components={externalMarkdownLink}>{feature.description}</Markdown></div>
              <div className="flex space-x-5 items-center justify-center mt-7">
                {
                  (currentPage === 0) ? null : (
                    <Button
                      className="w-1/3"
                      variant="outline" size="sm"
                      onClick={goToPrevPage}
                    >
                      上一页
                    </Button>
                  )
                }

                {
                  (currentPage === featureGuides.length - 1) ?
                    (
                      <Button
                        className="w-1/3"
                        variant="default"
                        size="sm"
                        onClick={() => onDone()}
                      >
                        我知道啦
                      </Button>
                    )
                    : (
                      <Button
                        className="w-1/3"
                        variant="default"
                        size="sm"
                        onClick={goToNextPage}
                      >
                        下一页
                      </Button>
                    )
                }
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MeetingGiftGuides = ({
  // url = publicAsset('/images/dashboard-guides-meeting-gift.png?v=2'),
  // url = 'https://oss.findinai.cn/asset/0226/banlance1.png',
  // url = "https://oss.findinai.cn/storage/20250418/c9b422a64f9cfa1030c2fb3f50ee85ae.png",
  url = "https://oss.findinai.cn/storage/20251218/d5e87d0dfcfb24e6935ef7490ace14ba.png",
  onDone = () => { },
}: {
  url?: string;
  onDone?: () => void;
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const scale = (Number(elRef.current?.offsetWidth) || 800) / 800;
    setScale(scale);
  }, []);
  return (
    <div ref={elRef} className="h-full w-full flex flex-col items-center justify-center overflow-hidden relative">
      <Image
        unoptimized={true}
        className=""
        src={url}
        width={802}
        height={554}
        alt="首次见面，送你 100 飞豆 免费体验"
      />
      <Button
        className="absolute w-[378px] h-[40px]"
        style={{
          scale,
          bottom: 40 * scale,
        }}
        onClick={() => onDone()}
      >
        立即使用
      </Button>
    </div>
  );
};

const MeetingCouponsGuides = ({
  url = "https://oss.findinai.cn/storage/20250418/270ca59e852e2d82543d3d398930e5e4.png",
  onDone = () => { },
}: {
  url?: string;
  onDone?: () => void;
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const scale = (Number(elRef.current?.offsetWidth) || 800) / 800;
    setScale(scale);
  }, []);
  return (
    <div ref={elRef} className="h-full w-full flex flex-col items-center justify-center overflow-hidden relative">
      <Image
        unoptimized={true}
        className=""
        src={url}
        width={969}
        height={831}
        alt="给新朋友一份礼物，文献综述首单减免400豆"
      />
      <Button
        className="absolute w-[240px] h-[48px]"
        style={{
          scale,
          bottom: 40 * scale,
        }}
        onClick={() => onDone()}
      >
        立即使用
      </Button>
    </div>
  );
};

export type UserTriggeredDialogProps = {
  children: ReactNode;
  height?: number;
  width?: number;
  url: string;
  description?: string;
  onDone?: () => void;
  label?: string;
}
export function UserTriggeredDialog({
  children,
  width = 1950,
  height = 1780,
  url = "https://oss.findinai.cn/storage/20250522/12604fbdb0bc26ee33de7fd2a75a1443.jpeg",
  description = '',
  onDone = () => { },
  label,
}: UserTriggeredDialogProps) {
  const [open, setOpen] = useState<boolean | undefined>(undefined);

  const renderGuidesContent = useCallback(() => {
    if (!url) {
      return <></>;
    }
    return (
      <CommonImageContent
        width={width}
        height={height}
        url={url}
        description={description}
        onDone={() => {
          setOpen(false);
          onDone();
        }}
        label={label}
      />
    )
  }, [url, width, height, description, label, onDone]);

  return (
    <Dialog
      open={!!open}
      onOpenChange={async (open) => {
        setOpen(open);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className={cn(
          "p-0 m-0",
          "w-fit h-auto sm:max-w-fit border-none bg-transparent focus-visible:outline-none focus-visible:ring-0 overflow-hidden gap-0",
        )}
        tabIndex={-1}
        onPointerDownOutside={(evt) => {
          evt.preventDefault();
        }}
        onOpenAutoFocus={(evt) => {
          evt.preventDefault();
        }}
      >
        {
          renderGuidesContent()
        }
        <DialogClose />
      </DialogContent>
    </Dialog>
  )
}

const CommonImageContent = ({
  width = 1950,
  height = 1780,
  url = "https://oss.findinai.cn/storage/20250522/12604fbdb0bc26ee33de7fd2a75a1443.jpeg",
  description = '',
  label = '',
  onDone = () => { },
}: {
  height?: number;
  width?: number;
  url?: string;
  description?: string;
  label?: string;
  onDone?: () => void;
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const x = (Number(elRef.current?.offsetWidth) || 800) / 800;
    const y = (Number(elRef.current?.offsetHeight) || 600) / 600;
    const scale = Math.min(x, y);
    setScale(scale);
  }, []);
  return (
    <div ref={elRef} className="h-full w-full flex flex-col items-center justify-center overflow-hidden relative">
      <Image
        unoptimized={true}
        className=""
        src={url}
        width={width}
        height={height}
        alt={description}
      />
      <Button
        size="lg"
        className="absolute w-[240px] h-[48px] text-lg"
        style={{
          scale,
          bottom: 40 * scale,
        }}
        onClick={() => onDone()}
      >
        {label || '我知道了'}
      </Button>
    </div>
  );
};
